import React, { useState, useEffect } from "react";
import { GetCreditPackages } from "./apis/get";
import { Zap, Check, Package, Flame } from "lucide-react";

interface CreditPackage {
  id: number;
  name: string;
  description: string;
  credits_amount: number;
  price_in_rupees: number;
  discounted_price_in_rupees: number;
  is_popular: boolean;
  platform: string;
}

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (packageId: number) => void | Promise<void>;
  title: string;
  userPlatform?: string;
}

const PaymentModal: React.FC<PaymentModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  userPlatform,
}) => {
  const [amount, setAmount] = useState<number>(0);
  const [packages, setPackages] = useState<CreditPackage[]>([]);
  const [selectedPackageId, setSelectedPackageId] = useState<number | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchPackages();
    }
  }, [isOpen]);

  const fetchPackages = async () => {
    try {
      const response = await GetCreditPackages();
      if (response.status === true || response.status === "success") {
        setPackages(response.data.packages);
      }
    } catch (error) {
      console.error("Failed to fetch packages in modal:", error);
    }
  };

  const handleSubmit = async () => {
    if (selectedPackageId) {
      await onConfirm(selectedPackageId);
      handleClose();
    }
  };

  const handleClose = () => {
    setAmount(0);
    setSelectedPackageId(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[500] p-4 animate-in fade-in duration-200">
      <div className="bg-[#0f172a] border border-slate-800 rounded-3xl p-8 w-full max-w-2xl shadow-2xl scale-in transform transition-all overflow-hidden max-h-[90vh] flex flex-col">
        
        <div className="flex justify-between items-center mb-8">
           <div>
              <h2 className="text-2xl font-black text-white italic uppercase tracking-tight">{title}</h2>
              <p className="text-slate-500 text-[10px] font-black uppercase tracking-widest mt-1">Direct Credit Grant • Admin Console</p>
           </div>
           <button onClick={handleClose} className="p-2 hover:bg-slate-800 rounded-xl text-slate-500 transition-colors">
              <Zap size={20} />
           </button>
        </div>

        <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar space-y-8">
          {/* Packages Selection */}
          <section>
            <div className="flex items-center gap-2 mb-4 text-indigo-400">
               <Package size={16} />
               <h3 className="text-xs font-black uppercase tracking-widest">Select {userPlatform?.toUpperCase()} Credit Bundle</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
               {packages
                 .filter(pkg => !userPlatform || pkg.platform.toLowerCase() === userPlatform.toLowerCase())
                 .map((pkg) => (
                 <div 
                   key={pkg.id}
                   onClick={() => {
                     setSelectedPackageId(pkg.id);
                     setAmount(pkg.discounted_price_in_rupees || pkg.price_in_rupees);
                   }}
                   className={`relative p-4 rounded-2xl border-2 transition-all cursor-pointer group flex flex-col justify-between ${
                     selectedPackageId === pkg.id 
                       ? 'bg-indigo-600/20 border-indigo-500 shadow-[0_0_20px_rgba(99,102,241,0.2)]' 
                       : 'bg-slate-900/50 border-slate-800 hover:border-slate-700'
                   }`}
                 >
                    {pkg.is_popular && (
                      <div className="absolute -top-2 -right-2 bg-amber-500 text-black text-[8px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full flex items-center gap-1 shadow-lg">
                        <Flame size={10} /> Popular
                      </div>
                    )}
                    
                    <div className="flex justify-between items-start mb-2">
                       <span className={`text-[10px] font-black uppercase tracking-widest ${selectedPackageId === pkg.id ? 'text-indigo-400' : 'text-slate-500'}`}>
                         {pkg.name.replace(/_/g, ' ')}
                       </span>
                       <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${pkg.platform.toLowerCase() === 'android' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-blue-500/10 text-blue-500'}`}>
                         {pkg.platform.toUpperCase()}
                       </span>
                    </div>

                    <div className="flex items-baseline gap-2 mb-1">
                       <span className="text-2xl font-black text-white italic">₹{pkg.discounted_price_in_rupees}</span>
                       <span className="text-[10px] font-bold text-slate-500">for {pkg.credits_amount} Credits</span>
                    </div>
                    
                    <div className="flex items-center gap-2 mt-auto">
                       <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${selectedPackageId === pkg.id ? 'bg-indigo-500 border-indigo-500' : 'border-slate-700'}`}>
                          {selectedPackageId === pkg.id && <Check size={10} className="text-white" />}
                       </div>
                    </div>
                 </div>
               ))}
            </div>
          </section>

        </div>

        <div className="pt-8 flex gap-4 mt-4 border-t border-slate-800">
          <button
            onClick={handleClose}
            className="flex-1 py-4 bg-slate-800/50 hover:bg-slate-800 text-slate-400 text-[10px] font-black uppercase tracking-[0.2em] rounded-2xl transition-all border border-slate-700 active:scale-95"
          >
            Abort
          </button>
          <button
            onClick={handleSubmit}
            disabled={!selectedPackageId}
            className="flex-1 py-4 bg-indigo-600 hover:bg-indigo-500 text-white text-[10px] font-black uppercase tracking-[0.2em] rounded-2xl shadow-xl shadow-indigo-600/20 active:scale-95 transition-all disabled:opacity-30 disabled:cursor-not-allowed group flex items-center justify-center gap-2"
          >
            <span>Grant Credits</span>
            <Check size={14} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;
